import {
  Card,
  CardContent,
  Grid,
  GridItem,
  Heading,
  PageArticle,
  Stack,
  Text,
} from "@generalapplications/ui";

import { PageHero } from "@/app/common/components/composite/layout/PageHero";
import { PageSection } from "@/app/common/components/composite/layout/PageSection";
import { OfferingList } from "@/app/common/components/composite/list/OfferingList";
import { RelatedLinkList } from "@/app/common/components/composite/navigation/RelatedLinkList";
import { ClosingBand } from "@/app/common/components/composite/layout/ClosingBand";
import {
  RevealGroup,
  RevealItem,
} from "@/app/common/components/composite/motion/RevealGroup";
import { ServiceSubsection } from "@/app/features/services/components/ServiceSubsection";
import { getCapabilityBySlug } from "@/content/capabilities";
import type { IndustryRecord } from "@/content/industries";
import { getServicesByIndustry } from "@/content/services";
import { routes } from "@/app/common/functions/routes/routes";
import { formatText } from "@/app/common/functions/format/formatText";

type IndustryEntry = { title: string; description: string };

type IndustryEntriesProps = {
  items: readonly IndustryEntry[];
};

/** The industry's engineering priorities, two to a row. */
function PriorityGrid({ items }: IndustryEntriesProps) {
  return (
    <RevealGroup>
      <Grid columns={{ base: 1, sm: 2 }} gap="lg">
        {items.map((entry) => (
          <RevealItem key={entry.title}>
            <Card radius="xxl" padding="lg" height="full">
              <CardContent gap="sm">
                <Heading level={3} size="md">
                  {formatText(entry.title)}
                </Heading>
                <Text leading="relaxed">{formatText(entry.description)}</Text>
              </CardContent>
            </Card>
          </RevealItem>
        ))}
      </Grid>
    </RevealGroup>
  );
}

/** Prior work in this industry: what it was, beside what it involved. */
function ExperienceList({ items }: IndustryEntriesProps) {
  return (
    <RevealGroup>
      <Stack gap="md">
        {items.map((entry) => (
          <RevealItem key={entry.title}>
            <Card radius="xxl" padding="lg">
              <Grid columns={{ base: 1, md: 3 }} gap="lg">
                <Heading level={3} size="md">
                  {formatText(entry.title)}
                </Heading>
                <GridItem span={{ base: 1, md: 2 }}>
                  <Text variant="prose">{formatText(entry.description)}</Text>
                </GridItem>
              </Grid>
            </Card>
          </RevealItem>
        ))}
      </Stack>
    </RevealGroup>
  );
}

export type IndustryTemplateProps = {
  industry: IndustryRecord;
};

export function IndustryTemplate({ industry }: IndustryTemplateProps) {
  const namedServices = getServicesByIndustry(industry.slug);

  const relatedCapabilityItems = industry.relatedCapabilities.flatMap(
    (slug) => {
      const related = getCapabilityBySlug(slug);
      return related
        ? [
            {
              label: related.title,
              href: routes.capabilities.detail(slug),
            },
          ]
        : [];
    },
  );

  return (
    <PageArticle rhythm="xl">
      <PageHero
        headline={industry.headline}
        copy={industry.heroCopy}
        image={industry.image}
      />

      <PageSection
        title={industry.operatingReality.title}
        body={industry.operatingReality.copy}
      />

      <PageSection title="Engineering Priorities">
        <PriorityGrid items={industry.engineeringPriorities} />
      </PageSection>

      <PageSection title="Where General Applications Can Help">
        <OfferingList
          items={industry.applicableWork}
          ariaLabel="Where General Applications can help"
        />

        <ServiceSubsection
          services={namedServices}
          copy="Specific, scoped work that applies in this environment. Each service page states how that work is grounded."
          ariaLabel="Services in this industry"
        />
      </PageSection>

      <PageSection
        title="Experience Behind the Work"
        subheading={
          industry.experienceIntro ??
          "General Applications draws on prior engineering experience across employment, consulting, independent product work, and current company projects."
        }
      >
        <ExperienceList items={industry.experience} />
      </PageSection>

      <PageSection
        title={industry.perspective.headline}
        body={[industry.perspective.copy]}
      />

      {relatedCapabilityItems.length > 0 && (
        <PageSection title="Related Capabilities">
          <RelatedLinkList
            items={relatedCapabilityItems}
            ariaLabel="Related capabilities"
          />
        </PageSection>
      )}

      <ClosingBand
        headline="Have a system that needs a clearer path forward?"
        copy={[
          "Bring the workflow, data problem, or operational constraint — we will help define the tradeoffs and the next practical move.",
        ]}
        cta={industry.cta}
        ctaHref={routes.contact}
      />
    </PageArticle>
  );
}
